import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { RelatedArticles } from '@/components/RelatedArticles';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Zap, Shield, Clock, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const AIAgentSalesforce = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');
  
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
    window.location.href = homeUrl;
  };

  return (
    <Layout>
      <SEOHead 
        title="Best AI Agent for Salesforce 2025: Voice-First CRM Automation | NoQ5.ai"
        description="NoQ5.ai: Voice-first Salesforce AI agent at €59/user/mo. Zero-setup, GDPR-compliant. Update any Salesforce object with voice. 7-day free trial."
        keywords="AI agent for Salesforce, Salesforce AI assistant, Agentforce alternative, voice AI for Salesforce, Salesforce automation, CRM AI agent, NoQ5, Gong alternative, Clari alternative"
        canonicalUrl="https://noq5.ai/ai-agent-salesforce"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "AI Agent for Salesforce Sales Teams – Complete Guide",
          "description": "How AI agents transform Salesforce for sales teams with voice-powered automation",
          "author": { "@type": "Organization", "name": "NoQ5.ai" },
          "publisher": { "@type": "Organization", "name": "NoQ5.ai" },
          "datePublished": "2025-10-09"
        }]}
      />
      <div className="container px-4 md:px-6 py-12 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs items={[
            { label: 'AI Agent for Salesforce', href: '/ai-agent-salesforce' }
          ]} />
          
          {/* Hero */}
          <div className="text-center mb-16">
            <div className="inline-block rounded-lg bg-oli-blue-light px-3 py-1 text-sm text-oli-blue mb-4">
              {t('aiAgent.badge')}
            </div>
            <div className="inline-block rounded-lg bg-amber-100 px-3 py-1 text-sm text-amber-700 mb-4 ml-2">
              ⏳ Salesforce Coming Soon
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text">
              {t('aiAgent.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              {t('aiAgent.description')}
            </p>
            <p className="text-lg text-oli-blue font-medium mb-8">
              ✅ HubSpot integration is live now! Salesforce integration coming very soon.
            </p>
            <Button 
              onClick={scrollToDemo}
              size="lg"
              className="bg-oli-blue hover:bg-oli-blue-dark text-lg px-8 py-4"
            >
              {t('aiAgent.cta')}
            </Button>
          </div>

          {/* TLDR Section */}
          <div className="bg-gradient-to-br from-oli-blue-light/10 to-oli-blue/5 rounded-2xl p-8 mb-16 border border-oli-blue-light/30">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Zap className="w-6 h-6 text-oli-blue" />
              {t('aiAgent.tldr.title')}
            </h2>
            <div className="space-y-3 text-gray-700">
              <p>
                ✅ {t('aiAgent.tldr.point1')}
              </p>
              <p>
                ✅ {t('aiAgent.tldr.point2')}
              </p>
              <p>
                ✅ {t('aiAgent.tldr.point3')}
              </p>
              <p>
                ✅ {t('aiAgent.tldr.point4')}
              </p>
            </div>
          </div>

          {/* What is an AI Agent? */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6">What is an AI Agent for Salesforce?</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-4">
                An <strong>AI agent for Salesforce</strong> is an intelligent assistant that automates repetitive CRM tasks using natural language processing and machine learning. Unlike basic automation tools that follow rigid rules, AI agents understand context and can handle complex, multi-step workflows.
              </p>
              <p className="mb-4">
                For sales teams, this means:
              </p>
              <ul className="space-y-2 mb-6">
                <li>✓ <strong>Automatic data entry</strong> – Speak your updates instead of typing them</li>
                <li>✓ <strong>Intelligent parsing</strong> – AI understands sales language and structures data correctly</li>
                <li>✓ <strong>Multi-object updates</strong> – Update contacts, opportunities, accounts, and custom objects simultaneously</li>
                <li>✓ <strong>Real-time sync</strong> – Changes appear in Salesforce instantly</li>
              </ul>
            </div>
          </section>

          {/* Why Sales Teams Need AI Agents */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Why Sales Teams Need AI Agents for Salesforce</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                <Clock className="w-10 h-10 text-oli-blue mb-4" />
                <h3 className="text-xl font-bold mb-3">Save Hours Every Week</h3>
                <p className="text-gray-600">
                  Sales reps lose hours every week typing into Salesforce. The AI agent eliminates that data entry so reps can focus on selling.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                <MessageSquare className="w-10 h-10 text-oli-blue mb-4" />
                <h3 className="text-xl font-bold mb-3">Better Data Quality</h3>
                <p className="text-gray-600">
                  When CRM updates are effortless, reps actually do them. Voice AI captures complete, accurate data while it's fresh in their minds.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                <Zap className="w-10 h-10 text-oli-blue mb-4" />
                <h3 className="text-xl font-bold mb-3">Mobile-First Updates</h3>
                <p className="text-gray-600">
                  Update Salesforce between meetings, in the car, or on the go. No need to wait until you're back at your desk.
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
                <Shield className="w-10 h-10 text-oli-blue mb-4" />
                <h3 className="text-xl font-bold mb-3">GDPR Compliance</h3>
                <p className="text-gray-600">
                  EU-based AI agents like NoQ5.ai keep your customer data in Europe with automatic anonymization to protect sensitive information.
                </p>
              </div>
            </div>
          </section>

          {/* How NoQ5.ai Works */}
          <section className="mb-16 bg-gray-50 rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-6 text-center">How NoQ5.ai AI Agent Works</h2>
            <div className="grid md:grid-cols-3 gap-8 mt-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-oli-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3">Speak Naturally</h3>
                <p className="text-gray-600">
                  Tell NoQ5.ai about your meeting, deal, or contact update in plain language, just like talking to a colleague.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-oli-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3">AI Processes</h3>
                <p className="text-gray-600">
                  Advanced NLP understands context, extracts key information, and structures data for Salesforce fields.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-oli-blue rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">Instant Update</h3>
                <p className="text-gray-600">
                  Native API writes directly to Salesforce, any object, any field. See changes in real-time.
                </p>
              </div>
            </div>
          </section>

          {/* Comparison with Traditional Methods */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-center">AI Agent vs Traditional Salesforce Data Entry</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-md">
                <thead>
                  <tr className="bg-oli-blue text-white">
                    <th className="py-4 px-6 text-left">Method</th>
                    <th className="py-4 px-6 text-left">Time Required</th>
                    <th className="py-4 px-6 text-left">Accuracy</th>
                    <th className="py-4 px-6 text-left">Mobile Friendly</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-medium">Manual Typing</td>
                    <td className="py-4 px-6 text-red-600">5-10 minutes</td>
                    <td className="py-4 px-6 text-yellow-600">Prone to errors</td>
                    <td className="py-4 px-6 text-red-600">❌ Difficult</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <td className="py-4 px-6 font-medium">Basic Voice-to-Text</td>
                    <td className="py-4 px-6 text-yellow-600">3-5 minutes</td>
                    <td className="py-4 px-6 text-yellow-600">Requires editing</td>
                    <td className="py-4 px-6 text-yellow-600">⚠️ Partial</td>
                  </tr>
                  <tr className="bg-oli-blue-light/10">
                    <td className="py-4 px-6 font-bold">NoQ5.ai AI Agent</td>
                    <td className="py-4 px-6 text-green-600 font-semibold">30 seconds</td>
                    <td className="py-4 px-6 text-green-600 font-semibold">AI-verified</td>
                    <td className="py-4 px-6 text-green-600 font-semibold">✅ Native</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Use Cases */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8">Real-World Use Cases</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-oli-blue">
                <h3 className="text-xl font-bold mb-2">Post-Meeting Updates</h3>
                <p className="text-gray-600 mb-3">
                  "Just finished a call with John at Acme Corp. They're interested in our Enterprise plan for $50K ARR. Next step is a technical demo next Tuesday. Add this as an opportunity in stage 2."
                </p>
                <p className="text-sm text-oli-blue font-semibold">→ AI creates opportunity, updates stage, schedules follow-up</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-oli-blue">
                <h3 className="text-xl font-bold mb-2">Contact Updates</h3>
                <p className="text-gray-600 mb-3">
                  "Met Sarah Johnson at the conference. She's Director of Sales at TechCo. Email is sarah@techco.com. Very interested in our product for Q2 implementation."
                </p>
                <p className="text-sm text-oli-blue font-semibold">→ AI creates contact, populates fields, adds notes</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-oli-blue">
                <h3 className="text-xl font-bold mb-2">Deal Progress</h3>
                <p className="text-gray-600 mb-3">
                  "Move the Adobe deal to stage 4. They signed the proposal. Expected close date is end of month. Probability 90%."
                </p>
                <p className="text-sm text-oli-blue font-semibold">→ AI updates opportunity stage, date, and probability</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <RelatedArticles 
            articles={[
              {
                title: "Best CRM AI Copilot for SMBs (2025)",
                description: "Compare top CRM AI copilots (Salesforce, HubSpot) and see why NoQ5.ai is the #1 choice for SMBs.",
                href: "/blog/best-salesforce-ai-copilot-smbs-2025"
              },
              {
                title: "GDPR-Compliant CRM AI Copilot",
                description: "NoQ5.ai is the only EU-based, GDPR-compliant AI Copilot for Salesforce and HubSpot with EU infrastructure.",
                href: "/blog/gdpr-compliant-salesforce-ai-copilot-europe"
              },
              {
                title: "NoQ5 vs Salesforce Einstein",
                description: "Detailed comparison of NoQ5.ai and Salesforce Einstein AI capabilities.",
                href: "/comparison/salesforce-einstein"
              },
              {
                title: "How to Update Salesforce Automatically",
                description: "Step-by-step guide to automating Salesforce updates with AI voice assistants.",
                href: "/blog/update-salesforce-automatically-2025"
              }
            ]}
          />

          {/* CTA */}
          <div className="bg-gradient-to-br from-oli-blue to-oli-blue-dark text-white p-12 rounded-2xl text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Salesforce Workflow?</h2>
            <p className="text-xl mb-8 opacity-90">
              Update Salesforce and HubSpot from voice, email and meetings. Quick setup, no workflow building.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToDemo}
                size="lg"
                className="bg-white text-oli-blue hover:bg-gray-100 text-lg px-8 py-4"
              >
                Start Free Trial
              </Button>
              <Button 
                onClick={() => window.location.href = '/comparison'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-4"
              >
                Compare AI Agents
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default AIAgentSalesforce;